import { useState, useEffect, useRef } from "react";

const STRIPE_LINK = "VOTRE_LIEN_STRIPE_ICI";

/* ─── DATA ─────────────────────────────────────────────── */
const MODULES = [
  { id:"01", icon:"🧠", title:"Fondations de l'IA", sub:"Comprendre sans jargon", desc:"Les bases claires et solides : comment fonctionne l'IA, les grands modèles, ce que ça change vraiment pour vous." },
  { id:"02", icon:"💬", title:"Prompt Engineering", sub:"Parler à l'IA comme un expert", desc:"Techniques avancées pour obtenir exactement ce que vous voulez. La compétence la plus rentable de la décennie." },
  { id:"03", icon:"⚡", title:"Productivité & Automatisation", sub:"Travailler 10× plus vite", desc:"Automatisez les tâches répétitives, rédigez en quelques secondes, analysez des données instantanément." },
  { id:"04", icon:"🎨", title:"Création avec l'IA", sub:"Images, vidéos, code, musique", desc:"Midjourney, Runway, Sora, GitHub Copilot. Devenez un créateur augmenté sans compétences techniques." },
  { id:"05", icon:"📈", title:"IA & Business", sub:"Stratégie et croissance", desc:"Intégrez l'IA dans vos processus, décuplez votre CA, prenez des décisions data-driven en temps réel." },
  { id:"06", icon:"🔬", title:"Niveau Expert", sub:"API, agents, fine-tuning", desc:"Construisez vos propres agents IA, utilisez les API, comprenez le fine-tuning. Top 1% des utilisateurs." },
];

const BENEFITS = [
  { icon:"⏱", title:"Gagnez 2h par jour", desc:"En automatisant vos tâches répétitives dès la première semaine." },
  { icon:"🚀", title:"Prenez 3 ans d'avance", desc:"Sur vos concurrents qui ignorent encore le potentiel de l'IA." },
  { icon:"💡", title:"Comprenez enfin l'IA", desc:"Sans être technique. En langage clair, avec des cas réels." },
  { icon:"🎯", title:"Appliquez immédiatement", desc:"Chaque module contient des exercices pratiques applicables le jour même." },
  { icon:"🔄", title:"Mises à jour à vie", desc:"L'IA évolue vite. Votre accès et vos mises à jour sont permanents." },
  { icon:"🛡", title:"Garantie 30 jours", desc:"Pas satisfait ? Remboursé intégralement, sans question." },
];

const PROFILES = [
  { icon:"💼", label:"Entrepreneur", desc:"Scalez votre business avec l'IA" },
  { icon:"👔", label:"Cadre / Manager", desc:"Décidez mieux, plus vite" },
  { icon:"✍️", label:"Créateur de contenu", desc:"10× plus de contenu, même qualité" },
  { icon:"💻", label:"Développeur", desc:"Codez plus vite, bugs en moins" },
  { icon:"🎓", label:"Étudiant", desc:"Longueur d'avance dès maintenant" },
  { icon:"🔄", label:"En reconversion", desc:"La compétence la plus demandée" },
];

const TESTIMONIALS = [
  { name:"Marie L.", role:"Directrice Marketing, Paris", quote:"En 3 semaines j'ai automatisé 60% de mes tâches. Mon équipe pensait que j'avais embauché quelqu'un. ROI immédiat.", stars:5, tag:"Productivité +60%" },
  { name:"Thomas B.", role:"Fondateur SaaS, Lyon", quote:"La formation la plus dense et la plus pratique que j'ai suivie. Zéro blabla. J'ai lancé mon agent IA en 2 jours.", stars:5, tag:"Agent IA créé" },
  { name:"Sophie R.", role:"Consultante RH, Bordeaux", quote:"Je partais de zéro. Maintenant je forme moi-même mes clients sur l'IA. En 30 jours, j'ai créé une nouvelle offre.", stars:5, tag:"Nouvelle offre lancée" },
  { name:"Karim M.", role:"Dev Senior, Toulouse", quote:"Même en tant que dev expérimenté, j'ai découvert des techniques que j'ignorais totalement. Niveau de contenu exceptionnel.", stars:5, tag:"Compétences avancées" },
];

const FAQS = [
  { q:"Est-ce adapté si je suis débutant complet ?", a:"Absolument. La formation commence de zéro, avec un langage simple et des exemples concrets. Aucune compétence technique requise — si vous savez utiliser un smartphone, vous pouvez suivre." },
  { q:"Combien de temps faut-il y consacrer par jour ?", a:"Entre 45 minutes et 1h30 par jour suffisent pour progresser. Les modules sont conçus pour s'intégrer dans un emploi du temps chargé." },
  { q:"Est-ce que j'ai un accès à vie ?", a:"Oui. Une fois acheté, le contenu vous appartient définitivement — y compris toutes les mises à jour futures, à vie, sans supplément." },
  { q:"Quels résultats concrets puis-je attendre ?", a:"La plupart des participants économisent 1 à 2 heures par jour dès la première semaine. En 30 jours, vous maîtrisez les outils qui transforment votre productivité." },
  { q:"Y a-t-il une garantie satisfait ou remboursé ?", a:"Oui, 30 jours sans condition. Si vous n'êtes pas satisfait, nous vous remboursons intégralement sur simple demande." },
  { q:"Le contenu est-il mis à jour régulièrement ?", a:"Oui. L'IA évolue rapidement — nous mettons à jour le contenu en permanence. Toutes les mises à jour sont incluses dans votre accès à vie." },
];

/* ─── HOOKS ────────────────────────────────────────────── */
function useInView(threshold = 0.15) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setInView(true); }, { threshold });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, inView];
}

function useCountUp(target, inView) {
  const [val, setVal] = useState(0);
  const done = useRef(false);
  useEffect(() => {
    if (!inView || done.current) return;
    done.current = true;
    let cur = 0;
    const step = target / 80;
    const t = setInterval(() => {
      cur += step;
      if (cur >= target) { setVal(target); clearInterval(t); }
      else setVal(Math.floor(cur));
    }, 16);
  }, [inView, target]);
  return val;
}

/* ─── PARTICLE CANVAS ──────────────────────────────────── */
function ParticleField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let W = canvas.width = window.innerWidth;
    let H = canvas.height = window.innerHeight;
    const particles = Array.from({ length: 80 }, () => ({
      x: Math.random() * W, y: Math.random() * H,
      vx: (Math.random() - 0.5) * 0.3, vy: (Math.random() - 0.5) * 0.3,
      r: Math.random() * 1.5 + 0.3,
      a: Math.random() * 0.5 + 0.1,
    }));
    let raf;
    const draw = () => {
      ctx.clearRect(0, 0, W, H);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = W; if (p.x > W) p.x = 0;
        if (p.y < 0) p.y = H; if (p.y > H) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(99,210,255,${p.a})`;
        ctx.fill();
      });
      particles.forEach((p, i) => {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = p.x - particles[j].x, dy = p.y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(p.x, p.y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.strokeStyle = `rgba(99,210,255,${0.08 * (1 - dist / 120)})`;
            ctx.lineWidth = 0.5;
            ctx.stroke();
          }
        }
      });
      raf = requestAnimationFrame(draw);
    };
    draw();
    const resize = () => { W = canvas.width = window.innerWidth; H = canvas.height = window.innerHeight; };
    window.addEventListener("resize", resize);
    return () => { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
}

/* ─── STAT COUNTER ─────────────────────────────────────── */
function StatCount({ target, suffix, label }) {
  const [ref, inView] = useInView(0.3);
  const val = useCountUp(target, inView);
  return (
    <div ref={ref} style={S.statItem}>
      <span style={S.statNum}>{val}{suffix}</span>
      <span style={S.statLabel}>{label}</span>
    </div>
  );
}

/* ─── SECTION WRAPPER ──────────────────────────────────── */
function Reveal({ children, delay = 0 }) {
  const [ref, inView] = useInView();
  return (
    <div ref={ref} style={{
      opacity: inView ? 1 : 0,
      transform: inView ? "translateY(0)" : "translateY(40px)",
      transition: `opacity 0.8s ease ${delay}s, transform 0.8s ease ${delay}s`,
    }}>
      {children}
    </div>
  );
}

/* ─── MAIN APP ──────────────────────────────────────────── */
export default function App() {
  const [openFaq, setOpenFaq] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [activeModule, setActiveModule] = useState(null);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);

  return (
    <div style={S.root}>
      <style>{CSS}</style>

      {/* ── NAV ── */}
      <nav style={{ ...S.nav, ...(scrolled ? S.navScrolled : {}) }}>
        <div style={S.navBrand}>
          <span style={S.navLogo}>ALPA</span>
          <span style={S.navLogoAccent}>FORMATION</span>
        </div>
        <a href={STRIPE_LINK} style={{...S.navCta, display:"none"}} className="nav-cta-mobile hover-lift">397€</a>
        <div style={S.navLinks} className="nav-links">
          <a href="#modules" style={S.navLink}>Programme</a>
          <a href="#temoignages" style={S.navLink}>Témoignages</a>
          <a href="#faq" style={S.navLink}>FAQ</a>
          <a href={STRIPE_LINK} style={S.navCta} className="hover-lift">Se connecter</a>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section style={S.hero}>
        <ParticleField />
        <div style={S.heroOrb1} />
        <div style={S.heroOrb2} />
        <div style={S.heroOrb3} />
        <div style={S.heroInner} className="hero-inner">
          <div style={S.heroLeft} className="hero-left hero-enter">
            <div style={S.heroBadge} className="hero-badge">
              <span style={S.heroBadgeDot} />
              Formation N°1 en France sur l'IA professionnelle
            </div>
            <h1 style={S.heroH1} className="hero-h1">
              Maîtrisez<br />
              <span style={S.heroGradient}>l'Intelligence</span><br />
              <span style={S.heroGradient}>Artificielle.</span>
            </h1>
            <p style={S.heroSub} className="hero-sub">
              De zéro à expert en 30 jours.<br />
              La méthode concrète pour utiliser l'IA dans votre vie professionnelle — sans être technique.
            </p>
            <div style={S.heroCtas} className="hero-ctas">
              <a href={STRIPE_LINK} style={S.heroPrimary} className="cta-pulse hover-lift">
                Commencer maintenant
                <span style={S.ctaPrice}>397€ accès à vie</span>
              </a>
              <a href="#modules" style={S.heroSecondary} className="hover-lift">
                Voir le programme →
              </a>
            </div>
            <div style={S.heroTrust} className="hero-trust">
              {["✓ Accès immédiat", "✓ Garantie 30 jours", "✓ Mises à jour à vie"].map((t, i) => (
                <span key={i} style={S.trustItem}>{t}</span>
              ))}
            </div>
          </div>

          <div style={S.heroRight} className="hero-right hero-enter-right">
            {/* Visual AI Card */}
            <div style={S.aiCard} className="ai-card float-card">
              <div style={S.aiCardGlow} />
              <div style={S.aiCardInner}>
                {/* AI Brain Visual */}
                <div style={S.brainVisual}>
                  <div style={S.brainCore}>
                    <span style={S.brainIcon}>🧠</span>
                  </div>
                  {[0,1,2,3,4,5].map(i => (
                    <div key={i} style={{ ...S.brainOrbit, animationDelay: `${i * 0.5}s`, transform: `rotate(${i * 60}deg) translateX(60px)` }} className="orbit-dot" />
                  ))}
                </div>
                <div style={S.aiCardStats}>
                  <div style={S.aiStat}><span style={S.aiStatNum}>1 200+</span><span style={S.aiStatL}>Étudiants formés</span></div>
                  <div style={S.aiStatDiv} />
                  <div style={S.aiStat}><span style={S.aiStatNum}>97%</span><span style={S.aiStatL}>Satisfaction</span></div>
                  <div style={S.aiStatDiv} />
                  <div style={S.aiStat}><span style={S.aiStatNum}>30j</span><span style={S.aiStatL}>Pour maîtriser</span></div>
                </div>
                <div style={S.progressBars}>
                  {[["Productivité", 94], ["Automatisation", 87], ["Création IA", 91]].map(([lbl, pct]) => (
                    <div key={lbl} style={S.progressRow}>
                      <span style={S.progressLabel}>{lbl}</span>
                      <div style={S.progressTrack}>
                        <div style={{ ...S.progressFill, width: `${pct}%` }} className="progress-fill" />
                      </div>
                      <span style={S.progressPct}>{pct}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div style={S.scrollHint} className="scroll-bounce">
          <div style={S.scrollLine} />
          <span style={S.scrollText}>Découvrir</span>
        </div>
      </section>

      {/* ── STATS BAR ── */}
      <section style={S.statsBar}>
        <div style={S.statsInner} className="stats-inner">
          <StatCount target={1200} suffix="+" label="Professionnels formés" />
          <div style={S.statsDivider} className="stats-divider" />
          <StatCount target={30} suffix=" jours" label="Pour maîtriser l'IA" />
          <div style={S.statsDivider} className="stats-divider" />
          <StatCount target={97} suffix="%" label="Taux de satisfaction" />
          <div style={S.statsDivider} className="stats-divider" />
          <StatCount target={6} suffix=" modules" label="De contenu expert" />
        </div>
      </section>

      {/* ── PROBLEM ── */}
      <section style={S.section}>
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Prise de conscience</div>
            <h2 style={S.sectionH2}>L'IA transforme tout.<br /><span style={S.gradText}>Êtes-vous prêt ?</span></h2>
            <p style={S.sectionSub}>Dans 24 mois, les professionnels qui maîtrisent l'IA seront dans une catégorie à part. La question n'est plus "si" — c'est "quand".</p>
          </Reveal>
          <div style={S.problemGrid} className="problem-grid">
            {[
              { icon:"⏳", title:"Vous perdez du temps", desc:"Sur des tâches que l'IA ferait en 10 secondes. Chaque jour, des heures gaspillées sur des automatisables." },
              { icon:"😰", title:"Vous vous sentez dépassé", desc:"Les termes GPT, Midjourney, agents IA... Vous entendez parler d'IA partout mais ne savez pas par où commencer." },
              { icon:"📉", title:"Vos concurrents avancent", desc:"Pendant que vous hésitez, d'autres automatisent, créent et se développent. L'écart se creuse chaque semaine." },
              { icon:"🔍", title:"Pas de méthode claire", desc:"Des tutos YouTube éparpillés. Des infos contradictoires. Impossible de construire une vraie compétence structurée." },
            ].map((item, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={S.problemCard} className="hover-card">
                  <div style={S.problemIcon}>{item.icon}</div>
                  <h3 style={S.problemTitle}>{item.title}</h3>
                  <p style={S.problemDesc}>{item.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── BENEFITS ── */}
      <section style={{ ...S.section, background: "linear-gradient(180deg, #040d1a 0%, #060e1f 100%)" }}>
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>La transformation</div>
            <h2 style={S.sectionH2}>Ce que vous allez<br /><span style={S.gradText}>accomplir</span></h2>
          </Reveal>
          <div style={S.benefitsGrid} className="benefits-grid">
            {BENEFITS.map((b, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <div style={S.benefitCard} className="hover-card">
                  <div style={S.benefitIconWrap}>
                    <span style={S.benefitIcon}>{b.icon}</span>
                  </div>
                  <h3 style={S.benefitTitle}>{b.title}</h3>
                  <p style={S.benefitDesc}>{b.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── MODULES ── */}
      <section style={S.section} id="modules">
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Programme complet</div>
            <h2 style={S.sectionH2}>30 jours.<br /><span style={S.gradText}>6 modules.</span> 1 transformation.</h2>
            <p style={S.sectionSub}>Chaque module est conçu pour une progression concrète et immédiatement applicable.</p>
          </Reveal>
          <div style={S.modulesGrid} className="modules-grid">
            {MODULES.map((m, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div
                  style={{ ...S.moduleCard, ...(activeModule === i ? S.moduleCardActive : {}) }}
                  className="hover-card"
                  onMouseEnter={() => setActiveModule(i)}
                  onMouseLeave={() => setActiveModule(null)}
                >
                  <div style={S.moduleGlow} />
                  <div style={S.moduleHeader}>
                    <div style={S.moduleNum}>{m.id}</div>
                    <div style={S.moduleDuration} className="module-tag">{m.icon}</div>
                  </div>
                  <h3 style={S.moduleTitle}>{m.title}</h3>
                  <div style={S.moduleSub}>{m.sub}</div>
                  <p style={S.moduleDesc}>{m.desc}</p>
                  <div style={S.moduleArrow} className="module-arrow">→</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOR WHO ── */}
      <section style={{ ...S.section, background: "linear-gradient(180deg, #040d1a 0%, #030a15 100%)" }}>
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Pour vous si...</div>
            <h2 style={S.sectionH2}>Cette formation est<br /><span style={S.gradText}>faite pour vous</span></h2>
          </Reveal>
          <div style={S.profilesGrid} className="profiles-grid">
            {PROFILES.map((p, i) => (
              <Reveal key={i} delay={i * 0.07}>
                <div style={S.profileCard} className="hover-card">
                  <div style={S.profileIcon}>{p.icon}</div>
                  <div style={S.profileLabel}>{p.label}</div>
                  <div style={S.profileDesc}>{p.desc}</div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── TESTIMONIALS ── */}
      <section style={S.section} id="temoignages">
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Ils ont transformé leur carrière</div>
            <h2 style={S.sectionH2}>Les résultats<br /><span style={S.gradText}>parlent d'eux-mêmes</span></h2>
          </Reveal>
          <div style={S.testimonialsGrid} className="testimonials-grid">
            {TESTIMONIALS.map((t, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <div style={S.testimonialCard} className="hover-card">
                  <div style={S.testimonialTag}>{t.tag}</div>
                  <div style={S.stars}>{"★".repeat(t.stars)}</div>
                  <p style={S.testimonialQuote}>"{t.quote}"</p>
                  <div style={S.testimonialAuthor}>
                    <div style={S.testimonialAvatar}>{t.name[0]}</div>
                    <div>
                      <div style={S.testimonialName}>{t.name}</div>
                      <div style={S.testimonialRole}>{t.role}</div>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── PRICING ── */}
      <section style={{ ...S.section, background: "linear-gradient(180deg, #030a15 0%, #040d1a 100%)" }}>
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Investissement</div>
            <h2 style={S.sectionH2}>Un prix unique.<br /><span style={S.gradText}>Un accès à vie.</span></h2>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={S.pricingWrapper}>
              <div style={S.pricingBorder}>
                <div style={S.pricingCard}>
                  <div style={S.pricingTop}>
                    <div style={S.pricingBadge}>Offre complète • Accès immédiat</div>
                    <div style={S.pricingAmount}>
                      <span style={S.pricingCur}>€</span>
                      <span style={S.pricingNum}>397</span>
                    </div>
                    <div style={S.pricingFreq}>Paiement unique — accès à vie</div>
                  </div>
                  <div style={S.pricingFeatures}>
                    {[
                      "6 modules complets (30+ heures)",
                      "Accès immédiat et permanent",
                      "Toutes les mises à jour incluses à vie",
                      "Exercices pratiques sur cas réels",
                      "Support par email prioritaire",
                      "Certificat de completion",
                      "Garantie satisfait ou remboursé 30 jours",
                    ].map((f, i) => (
                      <div key={i} style={S.pricingFeature}>
                        <span style={S.pricingCheck}>✓</span>
                        <span>{f}</span>
                      </div>
                    ))}
                  </div>
                  <a href={STRIPE_LINK} style={S.pricingCta} className="cta-pulse hover-lift">
                    Accéder à la formation maintenant
                  </a>
                  <p style={S.pricingGuarantee}>🔒 Paiement 100% sécurisé · Remboursé si non satisfait · Accès immédiat</p>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={S.section} id="faq">
        <div style={S.container}>
          <Reveal>
            <div style={S.sectionLabel}>Questions fréquentes</div>
            <h2 style={S.sectionH2}>Tout ce que vous<br /><span style={S.gradText}>voulez savoir</span></h2>
          </Reveal>
          <div style={S.faqList} className="faq-list">
            {FAQS.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div
                  style={{ ...S.faqItem, ...(openFaq === i ? S.faqItemOpen : {}) }}
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  className="hover-card"
                >
                  <div style={S.faqQ} className="faq-q">
                    <span>{f.q}</span>
                    <span style={{ ...S.faqIcon, transform: openFaq === i ? "rotate(45deg)" : "rotate(0)" }}>+</span>
                  </div>
                  {openFaq === i && (
                    <div style={S.faqA}>{f.a}</div>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ── FINAL CTA ── */}
      <section style={S.finalSection} className="final-section">
        <div style={S.finalGlow1} />
        <div style={S.finalGlow2} />
        <div style={S.container}>
          <Reveal>
            <div style={S.finalInner}>
              <div style={S.finalBadge}>🚀 Rejoignez 1 200+ professionnels</div>
              <h2 style={S.finalH2} className="final-h2">
                Votre avantage compétitif<br />
                <span style={S.gradText}>commence aujourd'hui.</span>
              </h2>
              <p style={S.finalSub}>
                Chaque jour qui passe est un jour de retard sur ceux qui maîtrisent déjà l'IA.<br />
                Prenez votre place dans le top 1% des professionnels augmentés.
              </p>
              <a href={STRIPE_LINK} style={S.finalCta} className="final-cta cta-pulse hover-lift">
                Commencer ma formation — 397€
                <span style={S.finalCtaSub}>Accès immédiat · Garantie 30 jours</span>
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={S.footer}>
        <div style={S.footerInner} className="footer-inner">
          <div>
            <div style={S.footerLogo}>ALPA<span style={{ color:"#63d2ff" }}>FORMATION</span></div>
            <p style={S.footerTagline}>La formation IA la plus complète de France</p>
          </div>
          <div style={S.footerLinks} className="footer-links">
            <a href="#modules" style={S.footerLink}>Programme</a>
            <a href="#faq" style={S.footerLink}>FAQ</a>
            <a href="mailto:contact@alpaformation.com" style={S.footerLink}>Contact</a>
          </div>
        </div>
        <div style={S.footerBottom} className="footer-bottom">
          <span>© 2025 AlpaFormation — Tous droits réservés</span>
          <span>contact@alpaformation.com</span>
        </div>
      </footer>
    </div>
  );
}

/* ─── STYLES ────────────────────────────────────────────── */
const S = {
  root: { background:"#030a15", color:"#e2eaf5", fontFamily:"'Outfit', 'DM Sans', sans-serif", minHeight:"100vh", overflowX:"hidden" },

  /* Nav */
  nav: { position:"fixed", top:0, left:0, right:0, zIndex:1000, display:"flex", justifyContent:"space-between", alignItems:"center", padding:"1.25rem 3rem", transition:"all 0.4s ease" },
  navScrolled: { background:"rgba(3,10,21,0.92)", backdropFilter:"blur(24px)", borderBottom:"1px solid rgba(99,210,255,0.08)", padding:"0.9rem 3rem" },
  navBrand: { display:"flex", alignItems:"center", gap:"0" },
  navLogo: { fontSize:"1.1rem", fontWeight:800, letterSpacing:"0.12em", color:"#e2eaf5" },
  navLogoAccent: { fontSize:"1.1rem", fontWeight:800, letterSpacing:"0.12em", color:"#63d2ff" },
  navLinks: { display:"flex", alignItems:"center", gap:"2.5rem" },
  navLink: { color:"rgba(226,234,245,0.6)", textDecoration:"none", fontSize:"0.875rem", fontWeight:500, letterSpacing:"0.02em", transition:"color 0.2s" },
  navCta: { background:"linear-gradient(135deg, #1a8fff, #63d2ff)", color:"#030a15", padding:"0.6rem 1.5rem", borderRadius:"6px", fontWeight:700, fontSize:"0.85rem", textDecoration:"none", letterSpacing:"0.03em", whiteSpace:"nowrap" },

  /* Hero */
  hero: { minHeight:"100vh", display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden", padding:"7rem 3rem 5rem" },
  heroOrb1: { position:"absolute", top:"10%", left:"20%", width:"500px", height:"500px", background:"radial-gradient(circle, rgba(26,143,255,0.12) 0%, transparent 65%)", pointerEvents:"none", borderRadius:"50%" },
  heroOrb2: { position:"absolute", bottom:"20%", right:"10%", width:"400px", height:"400px", background:"radial-gradient(circle, rgba(99,210,255,0.08) 0%, transparent 65%)", pointerEvents:"none", borderRadius:"50%" },
  heroOrb3: { position:"absolute", top:"50%", left:"50%", transform:"translate(-50%,-50%)", width:"800px", height:"400px", background:"radial-gradient(ellipse, rgba(26,143,255,0.04) 0%, transparent 70%)", pointerEvents:"none" },
  heroInner: { display:"grid", gridTemplateColumns:"1fr 1fr", gap:"5rem", alignItems:"center", maxWidth:"1200px", width:"100%", position:"relative", zIndex:1 },
  heroLeft: {},
  heroBadge: { display:"inline-flex", alignItems:"center", gap:"0.5rem", background:"rgba(99,210,255,0.08)", border:"1px solid rgba(99,210,255,0.2)", color:"rgba(99,210,255,0.9)", padding:"0.45rem 1rem", borderRadius:"50px", fontSize:"0.78rem", fontWeight:600, letterSpacing:"0.04em", marginBottom:"2rem" },
  heroBadgeDot: { width:"6px", height:"6px", borderRadius:"50%", background:"#63d2ff", boxShadow:"0 0 8px #63d2ff", flexShrink:0 },
  heroH1: { fontSize:"clamp(3rem, 5.5vw, 5.5rem)", fontWeight:800, lineHeight:1.0, letterSpacing:"-0.03em", color:"#f0f6ff", margin:"0 0 1.75rem" },
  heroGradient: { background:"linear-gradient(135deg, #1a8fff 0%, #63d2ff 50%, #a8e6ff 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" },
  heroSub: { fontSize:"1.1rem", lineHeight:1.75, color:"rgba(226,234,245,0.65)", marginBottom:"2.5rem", maxWidth:"480px" },
  heroCtas: { display:"flex", gap:"1rem", flexWrap:"wrap", marginBottom:"2rem" },
  heroPrimary: { display:"flex", flexDirection:"column", alignItems:"center", background:"linear-gradient(135deg, #1a8fff, #63d2ff)", color:"#030a15", padding:"1rem 2rem", borderRadius:"8px", fontWeight:800, fontSize:"1rem", textDecoration:"none", letterSpacing:"0.01em" },
  ctaPrice: { fontSize:"0.72rem", fontWeight:500, opacity:0.8, marginTop:"2px" },
  heroSecondary: { display:"flex", alignItems:"center", color:"rgba(226,234,245,0.7)", padding:"1rem 1.5rem", fontWeight:600, textDecoration:"none", border:"1px solid rgba(255,255,255,0.1)", borderRadius:"8px", fontSize:"0.95rem", transition:"all 0.2s" },
  heroTrust: { display:"flex", gap:"1.5rem", flexWrap:"wrap" },
  trustItem: { fontSize:"0.8rem", color:"rgba(99,210,255,0.7)", fontWeight:500 },

  /* Hero right card */
  heroRight: { display:"flex", justifyContent:"center" },
  aiCard: { position:"relative", width:"360px", borderRadius:"20px", overflow:"hidden" },
  aiCardGlow: { position:"absolute", inset:"-1px", background:"linear-gradient(135deg, rgba(99,210,255,0.3), rgba(26,143,255,0.1), rgba(99,210,255,0.05))", borderRadius:"20px", zIndex:0 },
  aiCardInner: { position:"relative", zIndex:1, background:"rgba(6,20,42,0.9)", backdropFilter:"blur(20px)", borderRadius:"19px", padding:"2.5rem" },
  brainVisual: { display:"flex", justifyContent:"center", alignItems:"center", height:"140px", position:"relative", marginBottom:"1.5rem" },
  brainCore: { width:"70px", height:"70px", borderRadius:"50%", background:"linear-gradient(135deg, rgba(26,143,255,0.3), rgba(99,210,255,0.1))", border:"1px solid rgba(99,210,255,0.4)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"2rem", boxShadow:"0 0 40px rgba(99,210,255,0.2)", zIndex:2 },
  brainOrbit: { position:"absolute", width:"10px", height:"10px", borderRadius:"50%", background:"#63d2ff", boxShadow:"0 0 10px #63d2ff", transformOrigin:"center" },
  aiCardStats: { display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:"1.5rem", padding:"1rem", background:"rgba(99,210,255,0.05)", borderRadius:"10px", border:"1px solid rgba(99,210,255,0.1)" },
  aiStat: { display:"flex", flexDirection:"column", alignItems:"center", gap:"0.2rem" },
  aiStatNum: { fontSize:"1.1rem", fontWeight:800, color:"#63d2ff" },
  aiStatL: { fontSize:"0.65rem", color:"rgba(226,234,245,0.5)", textAlign:"center" },
  aiStatDiv: { width:"1px", height:"30px", background:"rgba(99,210,255,0.15)" },
  progressBars: { display:"flex", flexDirection:"column", gap:"0.75rem" },
  progressRow: { display:"flex", alignItems:"center", gap:"0.75rem" },
  progressLabel: { fontSize:"0.72rem", color:"rgba(226,234,245,0.6)", width:"90px", flexShrink:0 },
  progressTrack: { flex:1, height:"4px", background:"rgba(99,210,255,0.1)", borderRadius:"2px", overflow:"hidden" },
  progressFill: { height:"100%", background:"linear-gradient(90deg, #1a8fff, #63d2ff)", borderRadius:"2px" },
  progressPct: { fontSize:"0.7rem", color:"#63d2ff", fontWeight:600, width:"30px", textAlign:"right" },

  scrollHint: { position:"absolute", bottom:"2.5rem", left:"50%", transform:"translateX(-50%)", display:"flex", flexDirection:"column", alignItems:"center", gap:"0.5rem", opacity:0.4 },
  scrollLine: { width:"1px", height:"40px", background:"linear-gradient(180deg, transparent, #63d2ff)" },
  scrollText: { fontSize:"0.7rem", letterSpacing:"0.15em", color:"#63d2ff", textTransform:"uppercase" },

  /* Stats bar */
  statsBar: { padding:"2.5rem 3rem", background:"rgba(99,210,255,0.03)", borderTop:"1px solid rgba(99,210,255,0.08)", borderBottom:"1px solid rgba(99,210,255,0.08)" },
  statsInner: { maxWidth:"900px", margin:"0 auto", display:"flex", justifyContent:"space-around", alignItems:"center", flexWrap:"wrap", gap:"1rem" },
  statItem: { display:"flex", flexDirection:"column", alignItems:"center", gap:"0.3rem" },
  statNum: { fontSize:"2.2rem", fontWeight:800, color:"#63d2ff", letterSpacing:"-0.02em" },
  statLabel: { fontSize:"0.75rem", color:"rgba(226,234,245,0.5)", letterSpacing:"0.05em", textTransform:"uppercase" },
  statsDivider: { width:"1px", height:"40px", background:"rgba(99,210,255,0.15)" },

  /* Sections */
  section: { padding:"7rem 3rem" },
  container: { maxWidth:"1200px", margin:"0 auto" },
  sectionLabel: { display:"inline-block", color:"#63d2ff", fontSize:"0.75rem", fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:"1rem", paddingBottom:"0.5rem", borderBottom:"1px solid rgba(99,210,255,0.3)" },
  sectionH2: { fontSize:"clamp(2rem, 3.5vw, 3.2rem)", fontWeight:800, lineHeight:1.15, letterSpacing:"-0.02em", color:"#f0f6ff", margin:"0 0 1.25rem" },
  sectionSub: { fontSize:"1.05rem", color:"rgba(226,234,245,0.6)", lineHeight:1.7, maxWidth:"600px", margin:"0 auto 4rem" },
  gradText: { background:"linear-gradient(135deg, #1a8fff 0%, #63d2ff 60%, #a8e6ff 100%)", WebkitBackgroundClip:"text", WebkitTextFillColor:"transparent", backgroundClip:"text" },

  /* Problem */
  problemGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(260px, 1fr))", gap:"1.5rem", marginTop:"3.5rem" },
  problemCard: { background:"rgba(6,15,30,0.8)", border:"1px solid rgba(255,100,100,0.12)", borderRadius:"14px", padding:"2rem", transition:"all 0.3s" },
  problemIcon: { fontSize:"2rem", marginBottom:"1rem" },
  problemTitle: { fontSize:"1rem", fontWeight:700, color:"#f0f6ff", margin:"0 0 0.6rem" },
  problemDesc: { fontSize:"0.875rem", color:"rgba(226,234,245,0.55)", lineHeight:1.7, margin:0 },

  /* Benefits */
  benefitsGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(300px, 1fr))", gap:"1.5rem", marginTop:"3.5rem" },
  benefitCard: { background:"rgba(6,20,42,0.6)", border:"1px solid rgba(99,210,255,0.1)", borderRadius:"14px", padding:"2rem", display:"flex", flexDirection:"column", gap:"0.75rem", transition:"all 0.3s" },
  benefitIconWrap: { width:"48px", height:"48px", borderRadius:"12px", background:"rgba(99,210,255,0.1)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:"1.4rem" },
  benefitIcon: {},
  benefitTitle: { fontSize:"1rem", fontWeight:700, color:"#f0f6ff", margin:0 },
  benefitDesc: { fontSize:"0.875rem", color:"rgba(226,234,245,0.55)", lineHeight:1.65, margin:0 },

  /* Modules */
  modulesGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(340px, 1fr))", gap:"1.5rem", marginTop:"3.5rem" },
  moduleCard: { background:"rgba(6,15,30,0.8)", border:"1px solid rgba(99,210,255,0.1)", borderRadius:"16px", padding:"2rem", position:"relative", overflow:"hidden", cursor:"default", transition:"all 0.3s" },
  moduleCardActive: { borderColor:"rgba(99,210,255,0.35)", background:"rgba(6,20,42,0.95)", transform:"translateY(-4px)", boxShadow:"0 20px 60px rgba(99,210,255,0.08)" },
  moduleGlow: { position:"absolute", top:0, left:0, right:0, height:"1px", background:"linear-gradient(90deg, transparent, rgba(99,210,255,0.4), transparent)", opacity:0 },
  moduleHeader: { display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:"1.25rem" },
  moduleNum: { fontSize:"2.5rem", fontWeight:900, color:"rgba(99,210,255,0.15)", lineHeight:1, letterSpacing:"-0.05em" },
  moduleDuration: { fontSize:"1.5rem" },
  moduleTitle: { fontSize:"1.05rem", fontWeight:800, color:"#f0f6ff", margin:"0 0 0.35rem" },
  moduleSub: { fontSize:"0.78rem", color:"#63d2ff", fontWeight:600, letterSpacing:"0.05em", textTransform:"uppercase", marginBottom:"0.75rem" },
  moduleDesc: { fontSize:"0.875rem", color:"rgba(226,234,245,0.55)", lineHeight:1.7, margin:"0 0 1rem" },
  moduleArrow: { color:"rgba(99,210,255,0.4)", fontSize:"1rem", fontWeight:700, transition:"all 0.3s" },

  /* Profiles */
  profilesGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(180px, 1fr))", gap:"1.25rem", marginTop:"3.5rem" },
  profileCard: { background:"rgba(6,15,30,0.8)", border:"1px solid rgba(99,210,255,0.1)", borderRadius:"14px", padding:"1.75rem 1.25rem", textAlign:"center", transition:"all 0.3s" },
  profileIcon: { fontSize:"2rem", marginBottom:"0.75rem" },
  profileLabel: { fontSize:"0.95rem", fontWeight:700, color:"#f0f6ff", marginBottom:"0.4rem" },
  profileDesc: { fontSize:"0.78rem", color:"rgba(226,234,245,0.5)", lineHeight:1.5 },

  /* Testimonials */
  testimonialsGrid: { display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(280px, 1fr))", gap:"1.5rem", marginTop:"3.5rem" },
  testimonialCard: { background:"rgba(6,15,30,0.8)", border:"1px solid rgba(99,210,255,0.1)", borderRadius:"16px", padding:"2rem", position:"relative", overflow:"hidden", transition:"all 0.3s" },
  testimonialTag: { display:"inline-block", background:"rgba(99,210,255,0.1)", border:"1px solid rgba(99,210,255,0.2)", color:"#63d2ff", padding:"0.2rem 0.7rem", borderRadius:"50px", fontSize:"0.7rem", fontWeight:700, letterSpacing:"0.05em", marginBottom:"1rem" },
  stars: { color:"#63d2ff", fontSize:"0.9rem", marginBottom:"1rem", letterSpacing:"0.1em" },
  testimonialQuote: { fontSize:"0.9rem", color:"rgba(226,234,245,0.75)", lineHeight:1.75, fontStyle:"italic", marginBottom:"1.5rem" },
  testimonialAuthor: { display:"flex", alignItems:"center", gap:"0.75rem" },
  testimonialAvatar: { width:"38px", height:"38px", borderRadius:"50%", background:"linear-gradient(135deg, #1a8fff, #63d2ff)", display:"flex", alignItems:"center", justifyContent:"center", fontWeight:800, color:"#030a15", fontSize:"0.9rem", flexShrink:0 },
  testimonialName: { fontSize:"0.875rem", fontWeight:700, color:"#f0f6ff" },
  testimonialRole: { fontSize:"0.75rem", color:"rgba(226,234,245,0.45)" },

  /* Pricing */
  pricingWrapper: { maxWidth:"580px", margin:"3.5rem auto 0" },
  pricingBorder: { padding:"1px", borderRadius:"20px", background:"linear-gradient(135deg, rgba(99,210,255,0.5), rgba(26,143,255,0.2), rgba(99,210,255,0.05))" },
  pricingCard: { background:"rgba(4,13,26,0.97)", borderRadius:"19px", padding:"3rem", textAlign:"center" },
  pricingTop: { marginBottom:"2rem" },
  pricingBadge: { display:"inline-block", background:"rgba(99,210,255,0.1)", color:"#63d2ff", padding:"0.3rem 1rem", borderRadius:"50px", fontSize:"0.75rem", fontWeight:700, letterSpacing:"0.08em", marginBottom:"1.5rem" },
  pricingAmount: { display:"flex", alignItems:"flex-start", justifyContent:"center", gap:"0.25rem", marginBottom:"0.5rem" },
  pricingCur: { fontSize:"2rem", fontWeight:700, color:"#f0f6ff", marginTop:"0.75rem" },
  pricingNum: { fontSize:"5.5rem", fontWeight:900, color:"#f0f6ff", lineHeight:1, letterSpacing:"-0.04em" },
  pricingFreq: { color:"rgba(226,234,245,0.45)", fontSize:"0.875rem" },
  pricingFeatures: { textAlign:"left", marginBottom:"2rem" },
  pricingFeature: { display:"flex", alignItems:"center", gap:"0.75rem", padding:"0.7rem 0", borderBottom:"1px solid rgba(99,210,255,0.06)", fontSize:"0.9rem", color:"rgba(226,234,245,0.8)" },
  pricingCheck: { color:"#63d2ff", fontWeight:900, flexShrink:0 },
  pricingCta: { display:"block", background:"linear-gradient(135deg, #1a8fff, #63d2ff)", color:"#030a15", padding:"1.15rem 2rem", borderRadius:"10px", fontWeight:800, fontSize:"1rem", textDecoration:"none", letterSpacing:"0.01em" },
  pricingGuarantee: { color:"rgba(226,234,245,0.35)", fontSize:"0.75rem", marginTop:"1rem" },

  /* FAQ */
  faqList: { maxWidth:"720px", margin:"3.5rem auto 0", display:"flex", flexDirection:"column", gap:"0.75rem" },
  faqItem: { background:"rgba(6,15,30,0.8)", border:"1px solid rgba(99,210,255,0.1)", borderRadius:"12px", padding:"1.5rem", cursor:"pointer", transition:"all 0.3s" },
  faqItemOpen: { borderColor:"rgba(99,210,255,0.3)", background:"rgba(6,20,42,0.95)" },
  faqQ: { display:"flex", justifyContent:"space-between", alignItems:"center", gap:"1rem", fontWeight:700, color:"#f0f6ff", fontSize:"0.95rem" },
  faqIcon: { color:"#63d2ff", fontSize:"1.5rem", fontWeight:300, lineHeight:1, transition:"transform 0.3s", flexShrink:0 },
  faqA: { marginTop:"1rem", color:"rgba(226,234,245,0.6)", lineHeight:1.75, fontSize:"0.875rem", paddingTop:"1rem", borderTop:"1px solid rgba(99,210,255,0.08)" },

  /* Final CTA */
  finalSection: { padding:"9rem 3rem", textAlign:"center", position:"relative", overflow:"hidden" },
  finalGlow1: { position:"absolute", top:"30%", left:"50%", transform:"translateX(-50%)", width:"700px", height:"400px", background:"radial-gradient(ellipse, rgba(26,143,255,0.1) 0%, transparent 70%)", pointerEvents:"none" },
  finalGlow2: { position:"absolute", top:"20%", left:"20%", width:"300px", height:"300px", background:"radial-gradient(circle, rgba(99,210,255,0.05) 0%, transparent 70%)", pointerEvents:"none" },
  finalInner: { position:"relative", zIndex:1 },
  finalBadge: { display:"inline-block", background:"rgba(99,210,255,0.08)", border:"1px solid rgba(99,210,255,0.2)", color:"rgba(99,210,255,0.8)", padding:"0.4rem 1.25rem", borderRadius:"50px", fontSize:"0.78rem", fontWeight:600, letterSpacing:"0.06em", marginBottom:"2rem" },
  finalH2: { fontSize:"clamp(2.2rem, 4vw, 4rem)", fontWeight:800, lineHeight:1.1, letterSpacing:"-0.03em", color:"#f0f6ff", marginBottom:"1.5rem" },
  finalSub: { fontSize:"1.05rem", color:"rgba(226,234,245,0.6)", lineHeight:1.8, marginBottom:"3rem" },
  finalCta: { display:"inline-flex", flexDirection:"column", alignItems:"center", background:"linear-gradient(135deg, #1a8fff, #63d2ff)", color:"#030a15", padding:"1.25rem 3rem", borderRadius:"10px", fontWeight:800, fontSize:"1.05rem", textDecoration:"none", letterSpacing:"0.01em" },
  finalCtaSub: { fontSize:"0.72rem", fontWeight:500, opacity:0.75, marginTop:"3px" },

  /* Footer */
  footer: { padding:"3rem", borderTop:"1px solid rgba(99,210,255,0.08)" },
  footerInner: { maxWidth:"1200px", margin:"0 auto", display:"flex", justifyContent:"space-between", alignItems:"flex-start", flexWrap:"wrap", gap:"2rem", marginBottom:"2rem" },
  footerLogo: { fontSize:"1rem", fontWeight:800, letterSpacing:"0.12em", marginBottom:"0.4rem" },
  footerTagline: { fontSize:"0.8rem", color:"rgba(226,234,245,0.35)", margin:0 },
  footerLinks: { display:"flex", gap:"2rem" },
  footerLink: { color:"rgba(226,234,245,0.4)", textDecoration:"none", fontSize:"0.85rem", transition:"color 0.2s" },
  footerBottom: { maxWidth:"1200px", margin:"0 auto", display:"flex", justifyContent:"space-between", flexWrap:"wrap", gap:"1rem", paddingTop:"1.5rem", borderTop:"1px solid rgba(99,210,255,0.06)", color:"rgba(226,234,245,0.25)", fontSize:"0.78rem" },
};

/* ─── CSS ANIMATIONS + RESPONSIVE ──────────────────────── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: smooth; }
  body { margin: 0; }

  .hero-enter { animation: heroSlideUp 1s cubic-bezier(0.22,1,0.36,1) forwards; }
  .hero-enter-right { animation: heroSlideRight 1s cubic-bezier(0.22,1,0.36,1) 0.2s both; }
  @keyframes heroSlideUp { from { opacity:0; transform:translateY(50px); } to { opacity:1; transform:translateY(0); } }
  @keyframes heroSlideRight { from { opacity:0; transform:translateX(50px); } to { opacity:1; transform:translateX(0); } }

  .float-card { animation: floatY 6s ease-in-out infinite; }
  @keyframes floatY { 0%,100% { transform: translateY(0px); } 50% { transform: translateY(-14px); } }

  .orbit-dot { animation: orbitPulse 2s ease-in-out infinite; }
  @keyframes orbitPulse { 0%,100% { opacity:0.3; transform: scale(0.8); } 50% { opacity:1; transform: scale(1.2); } }

  .progress-fill { animation: fillProgress 1.5s ease forwards; }
  @keyframes fillProgress { from { width:0; } }

  .cta-pulse { animation: ctaGlow 3s ease-in-out infinite; }
  @keyframes ctaGlow { 0%,100% { box-shadow: 0 0 0 0 rgba(99,210,255,0.4); } 50% { box-shadow: 0 0 0 14px rgba(99,210,255,0); } }

  .hover-lift { transition: transform 0.2s ease, box-shadow 0.2s ease; }
  .hover-lift:hover { transform: translateY(-3px); box-shadow: 0 12px 40px rgba(99,210,255,0.2); }

  .hover-card { transition: border-color 0.3s, transform 0.3s, box-shadow 0.3s; }
  .hover-card:hover { border-color: rgba(99,210,255,0.25) !important; transform: translateY(-4px); box-shadow: 0 24px 60px rgba(0,0,0,0.3); }

  .scroll-bounce { animation: scrollBounce 2s ease-in-out infinite; }
  @keyframes scrollBounce { 0%,100% { transform: translateX(-50%) translateY(0); } 50% { transform: translateX(-50%) translateY(8px); } }

  .module-arrow { transition: transform 0.3s, color 0.3s; }
  .hover-card:hover .module-arrow { transform: translateX(6px); color: #63d2ff !important; }

  /* ── RESPONSIVE MOBILE ── */
  @media (max-width: 768px) {

    /* Nav */
    nav { padding: 0.9rem 1.25rem !important; }
    .nav-links { display: none !important; }
    .nav-cta-mobile { display: flex !important; }

    /* Hero */
    .hero-inner { grid-template-columns: 1fr !important; gap: 2.5rem !important; padding: 0 !important; text-align: center; }
    .hero-left { display: flex; flex-direction: column; align-items: center; }
    .hero-badge { font-size: 0.7rem !important; }
    .hero-h1 { font-size: 2.6rem !important; }
    .hero-sub { font-size: 0.95rem !important; text-align: center; }
    .hero-ctas { flex-direction: column !important; align-items: center !important; width: 100%; }
    .hero-ctas a { width: 100%; max-width: 340px; justify-content: center; }
    .hero-trust { justify-content: center; gap: 0.75rem !important; flex-wrap: wrap; }
    .hero-right { justify-content: center; }
    .ai-card { width: 100% !important; max-width: 340px; }
    .float-card { animation: none; }

    /* Stats bar */
    .stats-inner { gap: 1.5rem !important; justify-content: center !important; }
    .stats-divider { display: none !important; }
    .stat-item { min-width: 80px; }

    /* Sections */
    section { padding: 4rem 1.25rem !important; }
    .section-h2 { font-size: 2rem !important; }
    .section-sub { font-size: 0.9rem !important; }

    /* Hero inner grid */
    .hero-inner { grid-template-columns: 1fr !important; gap: 2.5rem !important; text-align: center; }
    .hero-left { display: flex; flex-direction: column; align-items: center; }
    .problem-grid { grid-template-columns: 1fr !important; }
    .benefits-grid { grid-template-columns: 1fr !important; }
    .modules-grid { grid-template-columns: 1fr !important; }
    .profiles-grid { grid-template-columns: repeat(2, 1fr) !important; }
    .testimonials-grid { grid-template-columns: 1fr !important; }

    /* Pricing */
    .pricing-card { padding: 2rem 1.5rem !important; }
    .pricing-num { font-size: 4rem !important; }

    /* FAQ */
    .faq-list { margin: 2rem 0 0 !important; }
    .faq-q { font-size: 0.875rem !important; }

    /* Final CTA */
    .final-section { padding: 5rem 1.25rem !important; }
    .final-h2 { font-size: 2rem !important; }
    .final-cta { width: 100%; max-width: 340px; }

    /* Footer */
    .footer-inner { flex-direction: column !important; align-items: center !important; text-align: center; }
    .footer-links { justify-content: center !important; }
    .footer-bottom { flex-direction: column !important; align-items: center !important; gap: 0.5rem !important; }

    /* Scroll hint */
    .scroll-hint { display: none !important; }
  }

  @media (max-width: 480px) {
    .hero-h1 { font-size: 2.1rem !important; }
    .profiles-grid { grid-template-columns: 1fr !important; }
    .hero-badge { display: none; }
  }
`;
